import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import { visaApi } from "@/lib/visa";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, User, Stethoscope, Users, Plane, Upload } from "lucide-react";

const roleBase = (role) =>
  ({ admin: "/admin", coordinator: "/coordinator", partner: "/partner", patient: "/portal" }[role] || "/admin");

const STEPS = [
  { key: "patient", label: "Patient", icon: User },
  { key: "medical", label: "Medical", icon: Stethoscope },
  { key: "companions", label: "Companions", icon: Users },
  { key: "travel", label: "Travel", icon: Plane },
  { key: "documents", label: "Documents", icon: Upload },
];

const emptyForm = () => ({
  patient_info: { full_name: "", gender: "", date_of_birth: "", nationality: "", passport_number: "",
                   passport_expiry: "", country_of_residence: "", mobile_number: "", email: "" },
  medical_info: { patient_id: "", lead_id: "", diagnosis: "", department: "", hospital_id: "",
                   hospital_name: "", doctor_id: "", doctor_name: "", planned_procedure: "",
                   expected_admission_date: "", estimated_stay: "" },
  companions: [],
  travel_details: { proposed_arrival_date: "", proposed_departure_date: "",
                     arrival_airport: "", departure_airport: "", preferred_city: "",
                     accommodation_required: false, airport_pickup_required: false },
  coordinator_id: "",
  notes: "",
});

const emptyCompanion = () => ({
  id: (crypto.randomUUID && crypto.randomUUID()) || `c-${Date.now()}-${Math.random()}`,
  full_name: "", relationship: "", passport_number: "",
  passport_expiry: "", nationality: "",
});

export default function VisaRequestNew() {
  const { user } = useAuth();
  const base = roleBase(user?.role);
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [coordinators, setCoordinators] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [patients, setPatients] = useState([]);
  const [createdId, setCreatedId] = useState(null);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [coordsRes, hRes] = await Promise.all([
          user?.role !== "patient" ? api.get("/users?role=coordinator").then((r) => r.data).catch(() => []) : Promise.resolve([]),
          api.get("/hospitals").then((r) => r.data).catch(() => []),
        ]);
        setCoordinators(coordsRes);
        setHospitals(hRes);
      } catch (err) {
        console.warn("Failed to load coordinators/hospitals", err);
      }
    })();
  }, [user]);

  // Load patients for CRM auto-fill (for coord/admin/partner)
  useEffect(() => {
    if (user?.role === "patient") return;
    api.get("/patients").then((r) => setPatients(r.data)).catch((err) => {
      console.warn("Failed to load patients for CRM autofill", err);
    });
  }, [user]);

  const setField = (scope, field, value) => {
    setForm((f) => ({ ...f, [scope]: { ...f[scope], [field]: value } }));
  };

  const autofillFromPatient = (pid) => {
    const p = patients.find((x) => x.id === pid);
    if (!p) return;
    setForm((f) => ({
      ...f,
      patient_info: {
        ...f.patient_info,
        full_name: p.name || f.patient_info.full_name,
        email: p.email || f.patient_info.email,
        mobile_number: p.phone || f.patient_info.mobile_number,
        nationality: p.country || f.patient_info.nationality,
        country_of_residence: p.country || f.patient_info.country_of_residence,
      },
      medical_info: {
        ...f.medical_info,
        patient_id: p.id,
        lead_id: p.lead_id || "",
        hospital_id: p.hospital_id || f.medical_info.hospital_id,
        hospital_name: hospitals.find((h) => h.id === p.hospital_id)?.name || f.medical_info.hospital_name,
        planned_procedure: p.treatment || f.medical_info.planned_procedure,
      },
    }));
    toast.success("Auto-filled from patient CRM");
  };

  const canNext = () => {
    if (step === 0) return form.patient_info.full_name && form.patient_info.passport_number && form.patient_info.nationality;
    return true;
  };

  const submit = async (saveAsDraft = false) => {
    setSaving(true);
    try {
      const body = { ...form, status: saveAsDraft ? "draft" : form.companions.length && step === 4 ? "submitted" : "draft" };
      const created = await visaApi.createRequest(body);
      setCreatedId(created.id);
      toast.success(saveAsDraft ? "Draft saved — you can now upload documents" : "Visa request created");
      if (saveAsDraft) {
        // Move to document upload step so user can attach files
        setStep(4);
      }
      return created.id;
    } catch (e) {
      toast.error(getApiErrorMessage(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const submitAndUploadDone = async () => {
    const id = createdId || (await submit(false));
    if (id) navigate(`${base}/visa/${id}`);
  };

  const doUpload = async (file, category) => {
    let id = createdId;
    if (!id) {
      id = await submit(true);
      if (!id) return;
    }
    setUploading((u) => ({ ...u, [category]: 0 }));
    try {
      const useChunked = file.size > 5 * 1024 * 1024; // > 5MB → chunked
      if (useChunked) {
        await visaApi.chunkedUpload(id, file, category, null, (pct) =>
          setUploading((u) => ({ ...u, [category]: pct })));
      } else {
        await visaApi.uploadDoc(id, file, category, null, (pct) =>
          setUploading((u) => ({ ...u, [category]: pct })));
      }
      toast.success(`Uploaded: ${file.name}`);
      setUploading((u) => { const c = { ...u }; delete c[category]; return c; });
    } catch (e) {
      toast.error(getApiErrorMessage(e));
      setUploading((u) => { const c = { ...u }; delete c[category]; return c; });
    }
  };

  return (
    <div data-testid="visa-request-new-page">
      <PageHeader
        eyebrow="International Services"
        title="New Visa Invitation Request"
        subtitle="Complete the 5-step form to create an invitation letter workflow."
      />

      {/* Stepper */}
      <div className="bg-white border border-[#E8E6E1] rounded-2xl p-4 mb-6" data-testid="visa-stepper">
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => {
            const active = i === step;
            const done = i < step;
            const Icon = s.icon;
            return (
              <div key={s.key} className="flex-1 flex items-center">
                <button
                  type="button"
                  onClick={() => (i < step ? setStep(i) : null)}
                  className="flex items-center gap-3"
                  data-testid={`stepper-step-${s.key}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2
                    ${active ? "bg-[#2A4D3E] text-white border-[#2A4D3E]" :
                      done ? "bg-[#E6F0EB] text-[#2A4D3E] border-[#2A4D3E]" :
                      "bg-white text-[#8E9992] border-[#E8E6E1]"}`}>
                    {done ? <Check size={14} /> : <Icon size={14} />}
                  </div>
                  <div className={`text-xs font-medium ${active ? "text-[#2A332C]" : done ? "text-[#2A4D3E]" : "text-[#8E9992]"}`}>
                    {s.label}
                  </div>
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-3 ${done ? "bg-[#2A4D3E]" : "bg-[#E8E6E1]"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6 mb-4">
        {step === 0 && (
          <Step1Patient form={form} setField={setField} patients={patients} onAutofill={autofillFromPatient} />
        )}
        {step === 1 && (
          <Step2Medical form={form} setField={setField} hospitals={hospitals} setForm={setForm} />
        )}
        {step === 2 && (
          <Step3Companions form={form} setForm={setForm} />
        )}
        {step === 3 && (
          <Step4Travel form={form} setField={setField} />
        )}
        {step === 4 && (
          <Step5Documents doUpload={doUpload} uploading={uploading} coordinators={coordinators}
            coordinatorId={form.coordinator_id} setCoordinatorId={(v) => setForm((f) => ({ ...f, coordinator_id: v }))}
            userRole={user?.role} createdId={createdId}
          />
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
          className="text-sm text-[#5C6660] hover:text-[#2A332C] flex items-center gap-2 disabled:opacity-40"
          data-testid="stepper-back">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => submit(true)} disabled={saving}
            className="border border-[#E8E6E1] rounded-full px-5 py-2 text-sm text-[#5C6660] hover:bg-[#FAFAF7]"
            data-testid="save-draft-btn">
            Save draft
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Fill required fields")}
              className="bg-[#2A4D3E] text-white rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2"
              data-testid="stepper-next">
              Next <ArrowRight size={14} />
            </button>
          ) : (
            <button onClick={submitAndUploadDone} disabled={saving}
              className="bg-[#2A4D3E] text-white rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2"
              data-testid="visa-submit-btn">
              <Check size={14} /> Submit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Step components ----------
function Field({ label, children, required, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold mb-1.5">
        {label}{required ? " *" : ""}
      </div>
      {children}
    </label>
  );
}

const inputCls = "w-full border border-[#E8E6E1] rounded-lg px-3 py-2 text-sm focus:border-[#2A4D3E] outline-none";

function Step1Patient({ form, setField, patients, onAutofill }) {
  const p = form.patient_info;
  return (
    <div className="space-y-5">
      {patients?.length > 0 && (
        <div className="bg-[#F7F5F2] rounded-xl p-3 flex items-center gap-3">
          <div className="text-xs text-[#5C6660] font-semibold">Auto-fill from patient CRM:</div>
          <select onChange={(e) => e.target.value && onAutofill(e.target.value)} className={`${inputCls} max-w-xs`}>
            <option value="">Choose patient…</option>
            {patients.map((pt) => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
          </select>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Full Name (as in passport)" required testid="field-full-name">
          <input className={inputCls} value={p.full_name} onChange={(e) => setField("patient_info", "full_name", e.target.value)} data-testid="input-full-name" />
        </Field>
        <Field label="Gender" testid="field-gender">
          <select className={inputCls} value={p.gender || ""} onChange={(e) => setField("patient_info", "gender", e.target.value)} data-testid="input-gender">
            <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
          </select>
        </Field>
        <Field label="Date of Birth" testid="field-dob">
          <input type="date" className={inputCls} value={p.date_of_birth || ""} onChange={(e) => setField("patient_info", "date_of_birth", e.target.value)} data-testid="input-dob" />
        </Field>
        <Field label="Nationality" required testid="field-nationality">
          <input className={inputCls} value={p.nationality || ""} onChange={(e) => setField("patient_info", "nationality", e.target.value)} data-testid="input-nationality" />
        </Field>
        <Field label="Passport Number" required testid="field-passport">
          <input className={inputCls} value={p.passport_number || ""} onChange={(e) => setField("patient_info", "passport_number", e.target.value)} data-testid="input-passport" />
        </Field>
        <Field label="Passport Expiry" testid="field-passport-expiry">
          <input type="date" className={inputCls} value={p.passport_expiry || ""} onChange={(e) => setField("patient_info", "passport_expiry", e.target.value)} data-testid="input-passport-expiry" />
        </Field>
        <Field label="Country of Residence" testid="field-country">
          <input className={inputCls} value={p.country_of_residence || ""} onChange={(e) => setField("patient_info", "country_of_residence", e.target.value)} data-testid="input-country" />
        </Field>
        <Field label="Mobile Number" testid="field-mobile">
          <input className={inputCls} value={p.mobile_number || ""} onChange={(e) => setField("patient_info", "mobile_number", e.target.value)} data-testid="input-mobile" />
        </Field>
        <Field label="Email Address" testid="field-email">
          <input type="email" className={inputCls} value={p.email || ""} onChange={(e) => setField("patient_info", "email", e.target.value)} data-testid="input-email" />
        </Field>
      </div>
    </div>
  );
}

function Step2Medical({ form, setField, hospitals, setForm }) {
  const m = form.medical_info;
  const onHospital = (id) => {
    const h = hospitals.find((x) => x.id === id);
    setForm((f) => ({ ...f, medical_info: { ...f.medical_info, hospital_id: id, hospital_name: h?.name || "" }}));
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Treating Hospital"><select className={inputCls} value={m.hospital_id || ""} onChange={(e) => onHospital(e.target.value)} data-testid="input-hospital">
        <option value="">Select hospital</option>
        {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
      </select></Field>
      <Field label="Department"><input className={inputCls} value={m.department || ""} onChange={(e) => setField("medical_info", "department", e.target.value)} data-testid="input-department" /></Field>
      <Field label="Treating Doctor"><input className={inputCls} value={m.doctor_name || ""} onChange={(e) => setField("medical_info", "doctor_name", e.target.value)} data-testid="input-doctor" /></Field>
      <Field label="Diagnosis"><input className={inputCls} value={m.diagnosis || ""} onChange={(e) => setField("medical_info", "diagnosis", e.target.value)} data-testid="input-diagnosis" /></Field>
      <Field label="Planned Procedure"><input className={inputCls} value={m.planned_procedure || ""} onChange={(e) => setField("medical_info", "planned_procedure", e.target.value)} data-testid="input-procedure" /></Field>
      <Field label="Expected Admission Date"><input type="date" className={inputCls} value={m.expected_admission_date || ""} onChange={(e) => setField("medical_info", "expected_admission_date", e.target.value)} data-testid="input-admission-date" /></Field>
      <Field label="Estimated Hospital Stay"><input placeholder="e.g. 14 days" className={inputCls} value={m.estimated_stay || ""} onChange={(e) => setField("medical_info", "estimated_stay", e.target.value)} data-testid="input-stay" /></Field>
    </div>
  );
}

function Step3Companions({ form, setForm }) {
  const add = () => {
    if (form.companions.length >= 5) return toast.error("Maximum 5 companions");
    setForm((f) => ({ ...f, companions: [...f.companions, emptyCompanion()] }));
  };
  const update = (i, field, val) => setForm((f) => ({
    ...f, companions: f.companions.map((c, idx) => idx === i ? { ...c, [field]: val } : c)
  }));
  const remove = (i) => setForm((f) => ({ ...f, companions: f.companions.filter((_, idx) => idx !== i) }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#5C6660]">Up to 5 companions may travel with the patient.</div>
        <button onClick={add} type="button" className="border border-[#2A4D3E] text-[#2A4D3E] rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1.5 hover:bg-[#E6F0EB]" data-testid="add-companion-btn">
          <Plus size={12} /> Add companion
        </button>
      </div>
      {form.companions.length === 0 ? (
        <div className="bg-[#FAFAF7] border border-dashed border-[#E8E6E1] rounded-xl p-8 text-center text-sm text-[#8E9992]">
          No companions added yet.
        </div>
      ) : (
        <div className="space-y-3">
          {form.companions.map((c, i) => (
            <div key={c.id} className="border border-[#E8E6E1] rounded-xl p-4" data-testid={`companion-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold text-[#2A4D3E] uppercase tracking-wider">Companion #{i + 1}</div>
                <button onClick={() => remove(i)} className="text-[#C0392B] hover:opacity-70" data-testid={`remove-companion-${i}`}><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Full Name"><input className={inputCls} value={c.full_name} onChange={(e) => update(i, "full_name", e.target.value)} data-testid={`comp-name-${i}`} /></Field>
                <Field label="Relationship"><input placeholder="e.g. Spouse" className={inputCls} value={c.relationship} onChange={(e) => update(i, "relationship", e.target.value)} data-testid={`comp-rel-${i}`} /></Field>
                <Field label="Passport Number"><input className={inputCls} value={c.passport_number} onChange={(e) => update(i, "passport_number", e.target.value)} data-testid={`comp-pass-${i}`} /></Field>
                <Field label="Passport Expiry"><input type="date" className={inputCls} value={c.passport_expiry || ""} onChange={(e) => update(i, "passport_expiry", e.target.value)} /></Field>
                <Field label="Nationality"><input className={inputCls} value={c.nationality} onChange={(e) => update(i, "nationality", e.target.value)} /></Field>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Step4Travel({ form, setField }) {
  const t = form.travel_details;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Proposed Arrival Date"><input type="date" className={inputCls} value={t.proposed_arrival_date || ""} onChange={(e) => setField("travel_details", "proposed_arrival_date", e.target.value)} data-testid="input-arrival-date" /></Field>
      <Field label="Proposed Departure Date"><input type="date" className={inputCls} value={t.proposed_departure_date || ""} onChange={(e) => setField("travel_details", "proposed_departure_date", e.target.value)} data-testid="input-departure-date" /></Field>
      <Field label="Arrival Airport"><input placeholder="e.g. DEL" className={inputCls} value={t.arrival_airport || ""} onChange={(e) => setField("travel_details", "arrival_airport", e.target.value)} data-testid="input-arrival-airport" /></Field>
      <Field label="Departure Airport"><input className={inputCls} value={t.departure_airport || ""} onChange={(e) => setField("travel_details", "departure_airport", e.target.value)} /></Field>
      <Field label="Preferred City"><input className={inputCls} value={t.preferred_city || ""} onChange={(e) => setField("travel_details", "preferred_city", e.target.value)} /></Field>
      <div className="flex items-center gap-6 pt-6">
        <label className="flex items-center gap-2 text-sm text-[#2A332C]" data-testid="input-accommodation">
          <input type="checkbox" checked={t.accommodation_required || false} onChange={(e) => setField("travel_details", "accommodation_required", e.target.checked)} className="w-4 h-4" />
          Accommodation required
        </label>
        <label className="flex items-center gap-2 text-sm text-[#2A332C]" data-testid="input-pickup">
          <input type="checkbox" checked={t.airport_pickup_required || false} onChange={(e) => setField("travel_details", "airport_pickup_required", e.target.checked)} className="w-4 h-4" />
          Airport pickup required
        </label>
      </div>
    </div>
  );
}

function Step5Documents({ doUpload, uploading, coordinators, coordinatorId, setCoordinatorId, userRole, createdId }) {
  const cats = [
    { id: "passport", label: "Passport" },
    { id: "medical_report", label: "Medical Reports" },
    { id: "photograph", label: "Recent Photograph" },
    { id: "previous_visa", label: "Previous Visa (optional)" },
    { id: "other", label: "Other Supporting Documents" },
  ];
  return (
    <div className="space-y-5">
      {userRole !== "patient" && (
        <Field label="Assign Coordinator">
          <select className={inputCls} value={coordinatorId || ""} onChange={(e) => setCoordinatorId(e.target.value)} data-testid="input-coordinator">
            <option value="">Unassigned</option>
            {coordinators.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      )}
      <div className="bg-[#F7F5F2] border border-dashed border-[#E8E6E1] rounded-xl p-4 text-xs text-[#5C6660]">
        Save the request first (via &quot;Save draft&quot;) to upload documents. Supported types: PDF, JPG, PNG. Maximum 25 MB.
        {!createdId && <div className="mt-1 text-[#B26B00]">Tip: click &quot;Save draft&quot; below to unlock uploads.</div>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cats.map((c) => (
          <div key={c.id} className="border border-[#E8E6E1] rounded-xl p-4">
            <div className="text-sm font-medium text-[#2A332C] mb-2">{c.label}</div>
            <label className="cursor-pointer" data-testid={`upload-${c.id}-label`}>
              <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp"
                     disabled={!createdId}
                     onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0], c.id)}
                     data-testid={`upload-${c.id}-input`} />
              <div className={`w-full border border-dashed rounded-lg py-3 text-center text-xs font-medium
                ${createdId ? "border-[#2A4D3E] text-[#2A4D3E] hover:bg-[#E6F0EB]" : "border-[#E8E6E1] text-[#8E9992] opacity-60"}`}>
                <Upload size={14} className="inline mr-1" /> {uploading[c.id] != null ? `Uploading ${uploading[c.id]}%` : "Choose file"}
              </div>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
